/**
 * PILLAR 1 — Sheet Trust Bar.
 *
 * Iter 178 — REDESIGN.
 * The old inline rail rendered up to 4 red pills side-by-side in the
 * viewer chrome. On tablet widths this piled up as an anxiety wall of
 * red chips that fought the drawing for attention. The elite pattern
 * (Bluebeam, Procore, HCSS) is a SINGLE overall verdict pill with a
 * discreet "N issues" chevron that expands the details on demand.
 *
 * Behaviour:
 *   - ready              → 1 green pill, no expander
 *   - needs_action / not_ready → 1 severity pill + "N issues" chevron
 *                                → popover lists issues + inline CTAs
 *   - loading / error    → subtle mono chip
 *
 * Drawing-first: the strip is 36px tall, uses tokened colours, and
 * never wraps.
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, ChevronDown, HelpCircle, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/api";

// Tokens tuned for Field Black + Daylight parity; the outer pill uses a
// SOFT tinted background with a saturated border so it reads instantly
// without shouting like the old solid-red pill pile did.
const STATE_TOKEN = {
  ready: {
    dot: "#22c55e",
    bg: "rgba(34,197,94,0.10)",
    border: "rgba(34,197,94,0.55)",
    text: "#4ade80",
    Icon: Check,
    label: "READY",
  },
  needs_action: {
    dot: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.55)",
    text: "#fbbf24",
    Icon: AlertTriangle,
    label: "NEEDS ACTION",
  },
  not_ready: {
    dot: "#ef4444",
    bg: "rgba(239,68,68,0.12)",
    border: "rgba(239,68,68,0.55)",
    text: "#f87171",
    Icon: X,
    label: "NOT READY",
  },
  unknown: {
    dot: "#6b7280",
    bg: "rgba(107,114,128,0.10)",
    border: "rgba(107,114,128,0.35)",
    text: "#9ca3af",
    Icon: HelpCircle,
    label: "UNKNOWN",
  },
};

const CATEGORY_LABEL = {
  revision: "Revision",
  calibration: "Sheet measure",
  location: "Sheet location",
  stationing: "Station / offset",
  upload: "Upload",
  storage: "Storage",
};

function IssueRow({ category, block, onAction, onClose }) {
  const t = STATE_TOKEN[block?.state] || STATE_TOKEN.unknown;
  const Icon = t.Icon;
  const clickable = !!block?.action;
  return (
    <div
      className="flex items-start gap-2.5 rounded-md p-2.5 border border-[var(--fop-border)] bg-[var(--fop-surface-2)]/60"
      data-testid={`trust-issue-${category}`}
      data-trust-state={block?.state || "unknown"}
    >
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.text }}
      >
        <Icon size={12} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {CATEGORY_LABEL[category] || category}
        </div>
        <div className="text-[13px] font-medium text-[var(--fop-text)] leading-tight mt-0.5">
          {block?.label || t.label}
        </div>
        {block?.detail && (
          <div className="text-[12px] text-[var(--fop-text-2)] leading-snug mt-1">
            {block.detail}
          </div>
        )}
      </div>
      {clickable && (
        <button
          type="button"
          onClick={() => { onAction && onAction(block.action); onClose && onClose(); }}
          className="shrink-0 self-center min-h-[36px] px-3 rounded-md bg-[var(--fop-cta-bg)] text-white text-[11px] font-mono uppercase tracking-wider font-bold hover:bg-[var(--fop-cta-bg-hover)] transition-colors"
          data-testid={`trust-issue-cta-${category}`}
        >
          {block.action.label || "Fix"}
        </button>
      )}
    </div>
  );
}

export function TrustBar({
  sheetId,
  projectId,
  scope = "sheet",
  onAction,
  className = "",
}) {
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url =
      scope === "project"
        ? `/projects/${projectId}/trust`
        : `/sheets/${sheetId}/trust`;
    api
      .get(url)
      .then((r) => alive && setVerdict(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Trust unavailable"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [sheetId, projectId, scope]);

  const issues = useMemo(() => {
    if (!verdict) return [];
    const cats = ["revision", "calibration", "location", "stationing"];
    if (scope === "project") cats.push("upload");
    return cats
      .map((c) => ({ category: c, block: verdict[c] }))
      .filter((x) => x.block && x.block.state !== "ready" && x.block.state !== "unknown");
  }, [verdict, scope]);

  if (loading) {
    return (
      <div
        className={`inline-flex items-center gap-2 h-9 px-3 rounded-md text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] bg-[var(--fop-surface-2)]/50 border border-[var(--fop-border)] ${className}`}
        data-testid="trust-bar-loading"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-50 animate-pulse" />
        Checking trust…
      </div>
    );
  }
  if (err || !verdict) {
    return (
      <div
        className={`inline-flex items-center gap-2 h-9 px-3 rounded-md text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] ${className}`}
        data-testid="trust-bar-error"
      >
        {err || "Trust unavailable"}
      </div>
    );
  }

  const overallToken = STATE_TOKEN[verdict.overall] || STATE_TOKEN.unknown;
  const OverallIcon = overallToken.Icon;
  const hasIssues = issues.length > 0;

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid="trust-bar"
      data-trust-overall={verdict.overall}
    >
      {/* Overall verdict pill — single source of visual truth */}
      <span
        className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-[12px] font-mono uppercase tracking-wider font-bold shrink-0"
        style={{
          background: overallToken.bg,
          border: `1px solid ${overallToken.border}`,
          color: overallToken.text,
        }}
        data-testid="trust-bar-overall"
      >
        <OverallIcon size={13} strokeWidth={2.5} />
        {verdict.overall_label || overallToken.label}
      </span>

      {/* Expander — only when there ARE issues to expand */}
      {hasIssues && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-mono uppercase tracking-wider bg-[var(--fop-surface-2)]/60 hover:bg-[var(--fop-surface-3)] border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] transition-colors shrink-0"
              data-testid="trust-bar-expand"
              aria-label={`${issues.length} ${issues.length === 1 ? "issue" : "issues"} — expand`}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: overallToken.dot }}
                aria-hidden="true"
              />
              <span className="tabular-nums font-bold">{issues.length}</span>
              <span className="hidden sm:inline">
                {issues.length === 1 ? "issue" : "issues"}
              </span>
              <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-[380px] max-w-[92vw] p-2.5 bg-[var(--fop-surface)] border-[var(--fop-border)] shadow-2xl"
            data-testid="trust-bar-issues-panel"
          >
            <div className="px-1 pb-2 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  Sheet trust
                </div>
                <div className="text-[14px] font-bold text-[var(--fop-text)] mt-0.5">
                  {issues.length} {issues.length === 1 ? "item" : "items"} before this sheet is field-ready
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              {issues.map((it) => (
                <IssueRow
                  key={it.category}
                  category={it.category}
                  block={it.block}
                  onAction={onAction}
                  onClose={() => setOpen(false)}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export default TrustBar;
